/**
 * Grading the browser's own measurements.
 *
 * The thresholds are the platform's, deliberately: the result screen puts our numbers beside the
 * platform's, and two columns judged by different rules would disagree for reasons that have
 * nothing to do with the network.
 *
 * `unknown` is a first-class outcome. A signal nobody could measure must never be allowed to read
 * as a pass.
 */

import type { BrowserMeasurement } from './stats'
import type { Verdict } from './types'

export type Grade = 'ok' | 'degraded' | 'bad' | 'unknown'

/** The platform needs this much media before it will judge a leg. */
export const MIN_GRADED_MEDIA_MS = 5000

export const THRESHOLDS = {
	lossOkBelowPercent: 1,
	lossBadAbovePercent: 3,
	jitterOkBelowMs: 20,
	jitterBadAboveMs: 40,
	mesOkAtOrAbove: 80,
	mesBadBelow: 70,
	rttOkBelowMs: 150,
	rttBadAboveMs: 300
} as const

export function gradeLoss(percent: number | null): Grade {
	if (percent === null) return 'unknown'
	if (percent < THRESHOLDS.lossOkBelowPercent) return 'ok'
	if (percent > THRESHOLDS.lossBadAbovePercent) return 'bad'
	return 'degraded'
}

export function gradeJitter(ms: number | null): Grade {
	if (ms === null) return 'unknown'
	if (ms < THRESHOLDS.jitterOkBelowMs) return 'ok'
	if (ms > THRESHOLDS.jitterBadAboveMs) return 'bad'
	return 'degraded'
}

export function gradeMes(mes: number | null): Grade {
	if (mes === null) return 'unknown'
	if (mes >= THRESHOLDS.mesOkAtOrAbove) return 'ok'
	if (mes < THRESHOLDS.mesBadBelow) return 'bad'
	return 'degraded'
}

export function gradeRtt(ms: number | null): Grade {
	if (ms === null) return 'unknown'
	if (ms < THRESHOLDS.rttOkBelowMs) return 'ok'
	if (ms > THRESHOLDS.rttBadAboveMs) return 'bad'
	return 'degraded'
}

/** Worst measured signal wins; `unknown` only survives when nothing was measured. */
export function worstGrade(grades: Grade[]): Grade {
	if (grades.includes('bad')) return 'bad'
	if (grades.includes('degraded')) return 'degraded'
	if (grades.includes('ok')) return 'ok'
	return 'unknown'
}

export interface BrowserVerdict {
	verdict: Verdict
	measured: boolean
	reason?: 'LEG_TOO_SHORT'
	/** Which way was worse, so the sentence can name it. Null when neither stands out. */
	worseDirection: 'INBOUND' | 'OUTBOUND' | null
	grades: {
		inboundLoss: Grade
		inboundJitter: Grade
		outboundLoss: Grade
		outboundJitter: Grade
		rtt: Grade
	}
}

export function gradeBrowserCall(measurement: BrowserMeasurement, mediaMs: number): BrowserVerdict {
	const grades: BrowserVerdict['grades'] = {
		inboundLoss: gradeLoss(measurement.inboundLossPercent),
		inboundJitter: gradeJitter(measurement.inboundJitterMs),
		outboundLoss: gradeLoss(measurement.outboundLossPercent),
		outboundJitter: gradeJitter(measurement.outboundJitterMs),
		rtt: gradeRtt(measurement.rttMs)
	}

	const inbound = worstGrade([grades.inboundLoss, grades.inboundJitter])
	const outbound = worstGrade([grades.outboundLoss, grades.outboundJitter])
	const worseDirection = pickWorseDirection(inbound, outbound)

	// One-way audio outranks every quality number: no amount of good jitter makes a silent
	// direction acceptable, and it is the finding the visitor most needs to hear.
	if (!measurement.bothDirections) {
		return { verdict: 'NO_AUDIO', measured: true, worseDirection, grades }
	}

	if (mediaMs < MIN_GRADED_MEDIA_MS) {
		return {
			verdict: 'NOT_MEASURED',
			measured: false,
			reason: 'LEG_TOO_SHORT',
			worseDirection,
			grades
		}
	}

	// Reaching here means packets flowed both ways, so inbound loss is always a measured number
	// and `overall` is never 'unknown'.
	const overall = worstGrade(Object.values(grades))

	return {
		verdict: overall === 'bad' ? 'BAD' : overall === 'degraded' ? 'DEGRADED' : 'OK',
		measured: true,
		worseDirection,
		grades
	}
}

const SEVERITY: Record<Grade, number> = { ok: 0, unknown: 1, degraded: 2, bad: 3 }

function pickWorseDirection(inbound: Grade, outbound: Grade): 'INBOUND' | 'OUTBOUND' | null {
	if (SEVERITY[inbound] === SEVERITY[outbound]) return null
	return SEVERITY[inbound] > SEVERITY[outbound] ? 'INBOUND' : 'OUTBOUND'
}
